import { resourcePriceFixedFactory } from "@@/shared/acquire/resource-price/resource-price-fixed";
import { quantityPickerBookableOptionFactory } from "@@/shared/acquire/resource-reference-options/quantity-picker-bookable-option";
import type { ResourceReference } from "@@/shared/acquire/resource-references/resource-reference";
import { resourceReferenceFactory } from "@@/shared/acquire/resource-references/resource-reference";
import { resourceReferenceGroupFactory } from "@@/shared/acquire/resource-references/resource-reference-group";
import { resourceSharedFactory } from "@@/shared/acquire/resources/resource-shared";
import { metaContentFactory } from "@@/shared/meta-content/meta-content";
import { zeroPrice } from "@@/shared/prices/price";
import type { BookableTemplateQuantities } from "@@/shared/products/templates/product-templates/bookable-quantity.product-template";
import type { BookableProductTemplate } from "@@/shared/products/templates/product-templates/bookable.product-template";
import { svTranslator, translation } from "@@/shared/translations/translation";
import { units } from "@@/shared/units-and-quantities/unit";

const resourceReferenceFromTemplateFactory = (
    template: BookableProductTemplate,
) => {
    const quantities = template.bookableQuantities;
    if (quantities.length === 0) {
        return genericResourceReferenceFactory();
    }

    const references = quantities.map(fromBookableTemplateQuantity);
    // if (references.length === 1) references[0];

    const quantity = quantities[0];
    const group = resourceReferenceGroupFactory({
        meta: metaContentFactory({
            title: quantity.title,
            subtitle: quantity.subtitle,
        }),
        references: [...references],
        unit: references[0].resource.unit,
    });
    return group;
};

const fromBookableTemplateQuantity = (quantity: BookableTemplateQuantities) => {
    const defaultUnit = units.participant;

    const resource = resourceSharedFactory({
        meta: metaContentFactory({ title: quantity.title }),
        quantity: quantity.quantity,
        unit: quantity.unit
            ? {
                  ...defaultUnit,
                  name: {
                      ...defaultUnit.name,
                      sv: {
                          ...defaultUnit.name.sv,
                          singular: svTranslator(quantity.unit?.singular),
                          plural: svTranslator(quantity.unit?.plural),
                      },
                  },
              }
            : defaultUnit,
    });

    const resRef = resourceReferenceFactory({
        meta: metaContentFactory({
            title: quantity.title,
            subtitle: quantity.subtitle,
        }),

        options: [],
        resource: resource,
    });
    const options = quantity.bookableVariants.map(variant => {
        return quantityPickerBookableOptionFactory({
            meta: metaContentFactory({ title: variant.title }),
            resourceId: resRef.resource._id,
            resourceReferenceId: resRef._id,
            options: [],
            resourcePrice: variant.price,
            default: variant.defaultQuantity ?? 1,
            minMax: { min: variant.minQuantity, max: variant.maxQuantity },
        });
    });

    const resRefWithOption: ResourceReference = {
        ...resRef,
        options: [...options],
    };
    return resRefWithOption;
};

const genericResourceReferenceFactory = () => {
    const resourceShared = resourceSharedFactory({
        meta: metaContentFactory({ title: "" }),
        quantity: 10,
        unit: units.ticket,
    });
    const resourceReference = resourceReferenceFactory({
        meta: metaContentFactory({ title: "Biljetter" }),

        options: [],
        resource: resourceShared,
    });
    const quantityPickerBookableOption = quantityPickerBookableOptionFactory({
        meta: metaContentFactory({
            title: translation({ sv: "Vuxenbiljett", en: "Adult ticket" }),
        }),
        resourceId: resourceReference.resource._id,
        resourceReferenceId: resourceReference._id,
        options: [],
        resourcePrice: resourcePriceFixedFactory({
            price: zeroPrice,
        }),
        default: 1,
        minMax: { min: 1, max: 5 },
    });
    const resourceReferenceWithQuantityPickerBookableOption: ResourceReference =
        {
            ...resourceReference,
            options: [
                ...resourceReference.options,
                quantityPickerBookableOption,
            ],
        };
    return resourceReferenceWithQuantityPickerBookableOption;
};

export {
    fromBookableTemplateQuantity,
    genericResourceReferenceFactory,
    resourceReferenceFromTemplateFactory,
};
